import type { PublicDocId } from './api';
import { API_URL, FRONTEND_URL } from './consts';

export const ADMIN_INVITE_CREDITS_URL = `${API_URL}/admin/invite-credits`;
export const INVITE_DASHBOARD_URL = `${API_URL}/user/invites`;

/** Matches `PendingInviteResponse` in the Rust API. */
export interface PendingInvite {
  id: number;
  code: string;
  invitee_nickname: string;
  created_at: string;
  thread_context?: PublicDocId;
}

/** Matches `InviteDashboardResponse` in the Rust API. */
export interface InviteDashboard {
  credits_remaining: number;
  credits_requested_at: string | null;
  pending_invites: PendingInvite[];
}

/** Matches `CreateInviteRequest` in the Rust API. */
export interface CreateInviteRequest {
  inviter_topics: string[];
  invitee_topics: string[];
  nickname?: string;
  thread_context?: PublicDocId;
}

/** Matches `CreateInviteResponse` in the Rust API. */
export type CreateInviteResponse =
  | { status: 'success'; invite: PendingInvite }
  | { status: 'no_credits' }
  | { status: 'email_verification_required' };

/** Matches `CancelInviteRequest` in the Rust API. */
interface CancelInviteRequest {
  invite_id: number;
}

/** Matches `InviteCreditSummaryResponse` in the Rust API. */
export interface InviteCreditSummary {
  username: string;
  full_name: string | null;
  credits_remaining: number;
  credits_requested_at: string | null;
}

/** Matches `GrantInviteCreditsRequest` in the Rust API. */
interface GrantInviteCreditsRequest {
  quantity: number;
}

function requireSuccess(response: Response, message: string): void {
  if (!response.ok) {
    throw new Error(`${message} (HTTP ${response.status})`);
  }
}

async function readJson<T>(response: Response, message: string): Promise<T> {
  requireSuccess(response, message);
  return response.json() as Promise<T>;
}

export function buildInviteUrl(code: string): string {
  const path = `/register/${encodeURIComponent(code)}`;
  if (FRONTEND_URL) return new URL(path, FRONTEND_URL).toString();
  if (typeof window !== 'undefined' && /^https?:\/\//.test(window.location.origin)) {
    return new URL(path, window.location.origin).toString();
  }
  return path;
}

/** Copies an invitation URL, including in browsers where the asynchronous Clipboard API fails. */
export async function copyInviteUrl(code: string): Promise<void> {
  const inviteUrl = buildInviteUrl(code);
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      return;
    } catch {
      // The Clipboard API can be present but unavailable because of browser permissions.
    }
  }

  const textArea = document.createElement('textarea');
  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  textArea.value = inviteUrl;
  textArea.setAttribute('readonly', '');
  textArea.setAttribute('aria-hidden', 'true');
  textArea.tabIndex = -1;
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.append(textArea);
  textArea.select();
  try {
    if (!document.execCommand('copy')) throw new Error('Browser rejected clipboard copy');
  } finally {
    textArea.remove();
    previouslyFocused?.focus();
  }
}

export async function getInviteDashboard(): Promise<InviteDashboard> {
  const response = await fetch(INVITE_DASHBOARD_URL, { credentials: 'include' });
  return readJson(response, 'Failed to load invitations');
}

export async function createInvite(request: CreateInviteRequest): Promise<CreateInviteResponse> {
  const response = await fetch(INVITE_DASHBOARD_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return readJson(response, 'Failed to create invitation');
}

export async function cancelInvite(inviteId: number): Promise<void> {
  const request: CancelInviteRequest = { invite_id: inviteId };
  const response = await fetch(`${INVITE_DASHBOARD_URL}/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  requireSuccess(response, 'Failed to cancel invitation');
}

export async function requestInviteCredits(): Promise<void> {
  const response = await fetch(`${INVITE_DASHBOARD_URL}/request_more`, {
    method: 'POST',
    credentials: 'include',
  });
  requireSuccess(response, 'Failed to request invitation credits');
}

export async function getInviteCreditSummaries(): Promise<InviteCreditSummary[]> {
  const response = await fetch(ADMIN_INVITE_CREDITS_URL, { credentials: 'include' });
  return readJson(response, 'Failed to load invitation credits');
}

export async function grantInviteCredits(username: string, quantity: number): Promise<void> {
  const request: GrantInviteCreditsRequest = { quantity };
  const response = await fetch(
    `${API_URL}/admin/user/${encodeURIComponent(username)}/invite-credits`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  );
  requireSuccess(response, 'Failed to grant credits');
}
